import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux";
import { LocalStorageKeys } from "../../models/LocalStorageKeys";
import { SavedSentence } from "../../models/Sentence";
import store, { RootState } from "../../store/store";
import { FALLBACK_AVATAR } from "../../utils/enums";
import { getAverageFromList } from "../../utils/formatting";
import { previewActions } from "../Card/previewSlice";
import { gameActions } from "../Game/gameSlice";
import Logo from "../Logo/Logo";
import "./User.scss";
import Player from "../Players/Player";
import { PlayerPublic } from "../../models/Player";
import Dropdown from "../common/Dropdown";
import React from "react";
import Loading from "../common/Loading";
import UserHeading from "./UserHeading";

export default function PlayerStats() {

    const navigate = useNavigate();
    const allPlayersDropdownEnabled = true;

    const gameState = useAppSelector((state: RootState) => state.game);
    const userState = useAppSelector((state: RootState) => state.user);
    const [currentPlayerId, setCurrentPlayerId] = useState('');
    const localToken = localStorage.getItem(LocalStorageKeys.TOKEN_KEY);

    useEffect(() => {

        if (!localToken) {
            navigate(`/new`, { replace: true });
            return;
        }

        store.dispatch(gameActions.requestPlayersList());

        store.dispatch(gameActions.sendToken({
            token: localToken,
            gameId: undefined
        }));

    }, [gameState.isConnected, localToken, navigate]);

    useEffect(() => {

        if (!userState.statsUserData.id) {
            store.dispatch(gameActions.requestUserStats({
                playerId: userState.user.id,
            }));
        }

    }, [gameState.token, userState.user.id]);

    useEffect(() => {
        setCurrentPlayerId(userState.statsUserData.id);
    }, [gameState.token, userState.statsUserData.id]);

    useEffect(() => {
        document.addEventListener("keydown", keyPressed, false);

        return () => {
            document.removeEventListener("keydown", keyPressed, false);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const keyPressed = useCallback((event) => {
        switch (event.key) {
            case 'Escape':
                navigateToMatchmaking();
                break;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    function previewCards(card: string | undefined, reversedSentences: any[]) {
        if (!card) {
            console.warn(`card [${card}] not found`)
            return;
        }

        store.dispatch(previewActions.showPreview({
            card: { src: generateImagePath(card) }
        }));
    }

    function generateImagePath(imageName: string | undefined) {
        return `${gameState.settings?.cardsHost}${imageName}`;
    }

    const sentenceList: SavedSentence[] = [];

    let avgPlace = 0;
    let guessCount = 0;
    let score = 0;
    let turnCounter = 0;
    let guessScore = 0;
    let storytellerScore = 0;
    let storytellerVoteCount = 0;
    let successfulStorytellCount = 0;
    let failedStorytellerAllCount = 0;
    let failedStorytellerNoOneCount = 0;
    let failedStorytellerScore = 0;
    let matchCount = 0;
    let waitingForCount = 0;
    let maxScoreTurnCount = 0;
    let onlyGuesserCount = 0;

    let winnerCounter = 0;
    const places: number[] = [];

    userState.stats.forEach((stat) => {
        stat?.sentencePlayerGame?.forEach((sentence) => {
            if (sentence.card && !sentence.isDeleted) {
                sentenceList.push(sentence);
            }
        });

        if (stat.game.turnsPlayed) {
            turnCounter += stat.game.turnsPlayed;
        }

        if (stat.isWinner) {
            winnerCounter++;
        }

        if (stat.guessCount) {
            guessCount += stat.guessCount;
        }

        if (stat.matchCount) {
            matchCount += stat.matchCount;
        }

        if (stat.onlyGuesserCount) {
            onlyGuesserCount += stat.onlyGuesserCount;
        }

        if (stat.maxScoreTurnCount) {
            maxScoreTurnCount += stat.maxScoreTurnCount;
        }

        if (stat.waitingForCount) {
            waitingForCount += stat.waitingForCount;
        }

        if (stat.score) {
            score += stat.score;
        }

        if (stat.guessScore) {
            guessScore += stat.guessScore;
        }

        if (stat.storytellerScore) {
            storytellerScore += stat.storytellerScore;
        }

        if (stat.failedStorytellerScore) {
            failedStorytellerScore += stat.failedStorytellerScore;
        }

        if (stat.storytellerVoteCount) {
            storytellerVoteCount += stat.storytellerVoteCount;
        }

        if (stat.successfulStorytellCount) {
            successfulStorytellCount += stat.successfulStorytellCount;
        }

        if (stat.failedStorytellerNoOneCount) {
            failedStorytellerNoOneCount += stat.failedStorytellerNoOneCount;
        }

        if (stat.failedStorytellerAllCount) {
            failedStorytellerAllCount += stat.failedStorytellerAllCount;
        }

        if (stat.place) {
            places.push(stat.place);
        }

        if (places && places.length) {
            avgPlace = getAverageFromList(places);
        }

    });

    const storytellingTurns = sentenceList.length;
    const notStorytellingTurns = turnCounter - storytellingTurns;

    const playerData: Partial<PlayerPublic> = {
        username: userState.statsUserData.name,
        picture: userState.statsUserData.dixitAvatar ? userState.statsUserData.picture : FALLBACK_AVATAR,
        place: Number(avgPlace.toFixed(2)),
        guessCount: guessCount,
        matchCount: matchCount,
        score: score,
        guessScore,
        storytellerScore,
        storytellerVoteCount,
        successfulStorytellCount,
        failedStorytellerAllCount,
        failedStorytellerNoOneCount,
        failedStorytellerScore,
        waitingForCount,
        maxScoreTurnCount,
        onlyGuesserCount,
    };

    function navigateToMatchmaking() {
        navigate(`/`, { replace: true });
    }

    const handleSelect = (id: string) => {
        setCurrentPlayerId(id);

        store.dispatch(gameActions.requestUserStats({
            playerId: id,
        }));
    };

    return (
        <div>
            {!gameState.token && <><Logo /></>}
            {gameState.token && <>
                <UserHeading text="Stats" onClose={navigateToMatchmaking} />

                {allPlayersDropdownEnabled &&
                    <div className="dropdown-wrapper">
                        {userState?.user?.id &&
                            <Dropdown initialOption={userState.user} options={userState.players} onSelect={handleSelect} />
                        }
                    </div>
                }

                <div className="stats">
                    {(!userState.statsUserData.id || userState.statsUserData.id !== currentPlayerId) && <>
                        <Loading />
                    </>}
                    {(userState.statsUserData.id && userState.statsUserData.id === currentPlayerId) && <>
                        <div className="stats_player">
                            <Player player={playerData} />
                        </div>

                        <dl className="stats_list">

                            <dt>wins:</dt>
                            <dd>{winnerCounter}</dd>

                            <dt>games played:</dt>
                            <dd>{userState.stats.length}</dd>

                            <dt>turns played:</dt>
                            <dd>{turnCounter}</dd>

                            <dt>storytells:</dt>
                            <dd>{storytellingTurns}</dd>

                            <dt>sentences:</dt>
                            <dd>
                                {sentenceList.map((sentence, i) => {

                                    let failedLabel = '';

                                    if (!sentence.score || sentence.score === 0) {
                                        if (sentence.failedAll) {
                                            failedLabel = 'a';
                                        }

                                        if (sentence.failedNoOne) {
                                            failedLabel = 'n';
                                        }
                                    }

                                    return (
                                        <div key={i} className="stats_sentence">
                                            <span className="stats_sentence_heart">{sentence.topRated || sentence.likeCount ? `❤️${sentence.likeCount || 1}` : ''}</span>
                                            <a onClick={() => previewCards(sentence.card, sentenceList)}>
                                                "{sentence.sentence}"
                                            </a>

                                            <span className="stats_sentence_score">{sentence.score !== null ? `[${sentence.score}]` : ''}</span>
                                            {failedLabel && failedLabel.length &&
                                                <span className="stats_sentence_failed">{failedLabel}</span>
                                            }
                                            <div className="stats_sentence_explanation">{sentence.sentenceExplanation}</div>
                                        </div>
                                    )
                                })}
                            </dd>

                        </dl>
                    </>}

                </div>


            </>}
        </div>
    )
}
